import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { debounce } from 'lodash';
import AsyncSelect from 'react-select/async';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';

const SEARCH_INVENTORY_USERS_QUERY = gql`
  query SearchInventoryUsersForQuickNotes($searchTerm: String, $limit: Int) {
    searchInventoryUsersForQuickNotes(searchTerm: $searchTerm, limit: $limit) {
      id
      name
      branch
      email
    }
  }
`;

// Custom components for react-select
function NoOptionsMessage(props) {
  const { selectProps, innerProps } = props;
  return (
    <Typography
      color="textSecondary"
      className={selectProps.classes.noOptionsMessage}
      {...innerProps}
    >
      {selectProps.inputValue ? 'No users found.' : 'Start typing to search...'}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  const { selectProps, innerRef, children, innerProps } = props;
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: selectProps.classes.input,
          inputRef: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  const { innerRef, isFocused, isSelected, isDisabled, innerProps, data } = props;
  return (
    <MenuItem
      buttonRef={innerRef}
      selected={isFocused}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      {...innerProps}
    >
      <div>
        <Typography variant="body1" style={{ color: isDisabled ? '#999' : 'inherit' }}>
          {data.name}
          {isDisabled && ' (Limit reached)'}
        </Typography>
        {data.branch && (
          <Typography variant="caption" color="textSecondary">
            {data.branch}
          </Typography>
        )}
      </div>
    </MenuItem>
  );
}

function Placeholder(props) {
  const { selectProps, innerProps } = props;
  return (
    <Typography
      color="textSecondary"
      className={selectProps.classes.placeholder}
      {...innerProps}
    >
      {selectProps.placeholder}
    </Typography>
  );
}

function SingleValue(props) {
  const { selectProps, innerProps, data } = props;
  return (
    <Typography className={selectProps.classes.singleValue} {...innerProps}>
      {data.name}
      {data.branch && ` (${data.branch})`}
    </Typography>
  );
}

function MultiValue(props) {
  const { selectProps, data, removeProps } = props;
  const currentValue = selectProps.value || [];
  const isAtLimit = currentValue.length >= selectProps.maxSelections;
  const isNearLimit = currentValue.length >= selectProps.maxSelections - 2;
  
  return (
    <div 
      className={selectProps.classes.multiValue}
      style={{
        backgroundColor: isAtLimit ? selectProps.theme.palette.error.light :
                        isNearLimit ? selectProps.theme.palette.warning.light :
                        selectProps.theme.palette.grey[200],
        borderColor: isAtLimit ? selectProps.theme.palette.error.main :
                    isNearLimit ? selectProps.theme.palette.warning.main :
                    'transparent',
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    >
      <span className={selectProps.classes.multiValueLabel}>
        {data.name}
        {data.branch && ` (${data.branch})`}
      </span>
      <span className={selectProps.classes.multiValueRemove} {...removeProps}>
        ×
      </span>
    </div>
  );
}

function ValueContainer(props) {
  const { selectProps, children } = props;
  return <div className={selectProps.classes.valueContainer}>{children}</div>;
}

function Menu(props) {
  const { selectProps, innerProps, children } = props;
  return (
    <Paper square className={selectProps.classes.paper} {...innerProps}>
      {children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  MultiValue,
  ValueContainer,
};

class InventoryUserAutocomplete extends React.Component {
  constructor(props) {
    super(props);
    this.apolloClient = null;
  }

  reloadOptions = debounce((inputValue, callback) => {
    if (!this.apolloClient) {
      callback([]);
      return;
    }

    const searchTerm = inputValue || '';
    
    this.apolloClient
      .query({
        query: SEARCH_INVENTORY_USERS_QUERY,
        variables: { searchTerm, limit: 20 },
        fetchPolicy: 'network-only',
      })
      .then(response => {
        if (response.data && response.data.searchInventoryUsersForQuickNotes) {
          let options = response.data.searchInventoryUsersForQuickNotes.map(user => ({
            ...user,
            label: user.name,
            value: user.id,
          }));
          
          // De-duplicate search results to prevent duplicate options in dropdown
          options = this.deduplicateUsers(options);
          
          callback(options);
        } else {
          callback([]);
        }
      })
      .catch(error => {
        console.error('Error searching inventory users:', error);
        callback([]);
      });
  }, 300);

  /**
   * Comprehensive de-duplication function for selected users
   * De-duplicates based on multiple criteria to ensure data integrity
   */
  deduplicateUsers = (users) => {
    if (!Array.isArray(users)) return users;
    
    const originalLength = users.length;
    
    // Create a Map to track unique users by ID (most reliable identifier)
    const uniqueUsersMap = new Map();
    let duplicatesFound = 0;
    
    users.forEach(user => {
      if (user && user.id) {
        // Use ID as primary key, but also check for data consistency
        const existingUser = uniqueUsersMap.get(user.id);
        
        if (!existingUser) {
          // First occurrence of this user
          uniqueUsersMap.set(user.id, user);
        } else {
          // User already exists - this is a duplicate
          duplicatesFound++;
          
          // Ensure we keep the most complete data
          const mergedUser = {
            ...existingUser,
            ...user, // Override with newer data if any fields are missing
            id: user.id, // Ensure ID is preserved
          };
          uniqueUsersMap.set(user.id, mergedUser);
        }
      } else if (user) {
        // User without ID - this shouldn't happen but handle gracefully
        console.warn('InventoryUserAutocomplete: User found without ID, skipping:', user);
      }
    });
    
    const deduplicatedUsers = Array.from(uniqueUsersMap.values());
    
    // Log if duplicates were found and removed (helpful for debugging)
    if (duplicatesFound > 0) {
      console.info(`InventoryUserAutocomplete: Removed ${duplicatesFound} duplicate user(s) from selection`);
    }
    
    return deduplicatedUsers;
  };

  handleChange = item => {
    const { onChange, isMulti, maxSelections = 10 } = this.props;
    
    if (isMulti && Array.isArray(item)) {
      // First, de-duplicate the incoming selection
      const deduplicatedItems = this.deduplicateUsers(item);
      
      // Enforce maximum selection limit after de-duplication
      if (deduplicatedItems.length > maxSelections) {
        // If we exceed the limit, truncate to the maximum allowed
        // This handles cases where multiple users might be selected simultaneously
        const limitedItems = deduplicatedItems.slice(0, maxSelections);
        onChange(limitedItems);
        return;
      }
      
      onChange(deduplicatedItems);
    } else {
      onChange(item);
    }
  };

  handleClearAll = () => {
    const { onChange, isMulti } = this.props;
    if (isMulti) {
      onChange([]);
    }
  };

  handleRemoveUser = (userToRemove) => {
    const { onChange, isMulti, value } = this.props;
    if (isMulti && Array.isArray(value)) {
      const filteredUsers = value.filter(user => user.id !== userToRemove.id);
      onChange(filteredUsers);
    }
  };

  renderSelectionManagement = (deduplicatedValue, selectionCount, maxSelections, classes, theme) => {
    if (!Array.isArray(deduplicatedValue) || deduplicatedValue.length === 0) {
      return null;
    }

    return (
      <Box className={classes.selectionManagement}>
        <Box className={classes.selectionHeader}>
          <Chip
            size="small"
            label={`${selectionCount} of ${maxSelections} selected`}
            color={selectionCount >= maxSelections ? 'secondary' : 'primary'}
            className={classes.countChip}
          />
          <Button
            size="small"
            onClick={this.handleClearAll}
            className={classes.clearAllButton}
            disabled={selectionCount === 0}
          >
            Clear All
          </Button>
        </Box>
        
        <Box className={classes.selectedUsers}>
          {deduplicatedValue.map((user, index) => (
            <Chip
              key={user.id || index}
              label={`${user.name}${user.branch ? ` (${user.branch})` : ''}`}
              onDelete={() => this.handleRemoveUser(user)}
              size="small"
              className={classes.userChip}
              color="default"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
    );
  };

  render() {
    const { classes, theme, label, value, placeholder, error, helperText, isMulti, maxSelections = 10, showSelectionManagement = true } = this.props;
    
    // Ensure the value is always de-duplicated, even if passed from parent
    const deduplicatedValue = isMulti && Array.isArray(value) ? this.deduplicateUsers(value) : value;
    
    // Calculate selection count for multi-select
    const selectionCount = isMulti && Array.isArray(deduplicatedValue) ? deduplicatedValue.length : 0;
    const isAtLimit = isMulti && selectionCount >= maxSelections;
    const isNearLimit = isMulti && selectionCount >= maxSelections - 2; // Within 2 of limit
    
    // Create comprehensive helper text with visual feedback
    let updatedHelperText = helperText;
    let selectionStatus = '';
    
    if (isMulti) {
      if (isAtLimit) {
        selectionStatus = `Maximum ${maxSelections} users selected. Remove users to select others.`;
      } else if (isNearLimit) {
        selectionStatus = `${selectionCount}/${maxSelections} users selected. ${maxSelections - selectionCount} remaining.`;
      } else {
        selectionStatus = `${selectionCount}/${maxSelections} users selected`;
      }
      
      updatedHelperText = selectionStatus + (helperText ? ` | ${helperText}` : '');
    }

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        margin: '2px 0',
        paddingBottom: 0,
        paddingTop: 0,
        '& input': {
          font: 'inherit',
        },
      }),
      control: (base, state) => ({
        ...base,
        minHeight: isMulti ? 'auto' : base.minHeight, // Auto height for multi-select
        borderColor: isAtLimit ? theme.palette.error.main :
                    isNearLimit ? theme.palette.warning.main :
                    state.isFocused ? theme.palette.primary.main :
                    base.borderColor,
        '&:hover': {
          borderColor: isAtLimit ? theme.palette.error.main :
                      isNearLimit ? theme.palette.warning.main :
                      theme.palette.primary.main,
        },
        // Ensure proper spacing when multi-select
        ...(isMulti && {
          padding: '2px',
          flexWrap: 'wrap',
        }),
      }),
      valueContainer: base => ({
        ...base,
        padding: isMulti ? '2px 8px' : base.padding,
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        minHeight: isMulti ? 32 : base.minHeight,
      }),
      multiValue: base => ({
        ...base,
        margin: '1px 2px',
      }),
      placeholder: base => ({
        ...base,
        margin: isMulti ? '2px 0' : base.margin,
      }),
    };

    return (
      <Query query={gql`query { __typename }`}>
        {({ client }) => {
          this.apolloClient = client;
          return (
            <div className={classes.root}>
              <AsyncSelect
                classes={classes}
                styles={selectStyles}
                value={deduplicatedValue}
                theme={theme}
                maxSelections={maxSelections}
                textFieldProps={{
                  label,
                  error: error || isAtLimit,
                  helperText: updatedHelperText,
                  InputLabelProps: {
                    shrink: true,
                  },
                  FormHelperTextProps: {
                    style: {
                      color: isAtLimit ? theme.palette.error.main : 
                             isNearLimit ? theme.palette.warning.main : 
                             theme.palette.text.secondary
                    }
                  },
                }}
                loadOptions={this.reloadOptions}
                defaultOptions
                components={components}
                placeholder={placeholder}
                onChange={this.handleChange}
                isClearable
                isMulti={isMulti}
                closeMenuOnSelect={!isMulti}
                isOptionDisabled={() => isAtLimit}
              />
              
              {/* Selection Management UI for multi-select */}
              {isMulti && showSelectionManagement && this.renderSelectionManagement(
                deduplicatedValue, 
                selectionCount, 
                maxSelections, 
                classes, 
                theme
              )}
            </div>
          );
        }}
      </Query>
    );
  }
}

InventoryUserAutocomplete.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  isMulti: PropTypes.bool,
  maxSelections: PropTypes.number,
  showSelectionManagement: PropTypes.bool,
};

InventoryUserAutocomplete.defaultProps = {
  label: 'Select User',
  value: null,
  placeholder: 'Search by name or branch...',
  error: false,
  helperText: '',
  isMulti: false,
  maxSelections: 10,
  showSelectionManagement: true,
};

const styles = theme => ({
  root: {
    flexGrow: 1,
    width: '100%',
  },
  input: {
    display: 'flex',
    paddingLeft: 9,
    color: '#777',
    height: 'auto',
    minHeight: 'auto',
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'visible', // Allow multi-select values to be visible
    minHeight: 38, // Ensure minimum height for proper display
    padding: '6px 0 2px', // Adjust padding for multi-select
    gap: theme.spacing.unit / 2, // Space between multi-select chips
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
    background: 'white',
  },
  multiValue: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius * 0.75, // Slightly smaller radius
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    minWidth: 0,
    margin: '2px 4px 2px 0', // Tighter margins for compact display
    fontSize: 13, // Slightly smaller font for form integration
    maxHeight: 28, // Consistent height
    border: `1px solid ${theme.palette.primary.main}`,
    transition: 'all 0.2s ease-in-out',
  },
  multiValueLabel: {
    borderRadius: `${theme.shape.borderRadius * 0.75}px 0 0 ${theme.shape.borderRadius * 0.75}px`,
    padding: '4px 6px', // More compact padding
    paddingRight: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '150px', // Slightly smaller max width for form context
    color: theme.palette.primary.contrastText,
    fontSize: 'inherit',
    lineHeight: 1.2,
  },
  multiValueRemove: {
    borderRadius: `0 ${theme.shape.borderRadius * 0.75}px ${theme.shape.borderRadius * 0.75}px 0`,
    padding: '4px 6px',
    cursor: 'pointer',
    color: theme.palette.primary.contrastText,
    fontSize: '14px',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      color: '#fff',
    },
  },
  selectionManagement: {
    marginTop: theme.spacing.unit * 1.5,
    padding: `${theme.spacing.unit * 1.5}px ${theme.spacing.unit}px`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    transition: 'box-shadow 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[2],
    },
  },
  selectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.unit,
    paddingBottom: theme.spacing.unit / 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  countChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
  },
  clearAllButton: {
    fontSize: '0.75rem',
    minWidth: 'auto',
    padding: `${theme.spacing.unit / 2}px ${theme.spacing.unit}px`,
    borderRadius: theme.shape.borderRadius / 2,
    textTransform: 'none',
    fontWeight: 500,
    '&:disabled': {
      color: theme.palette.text.disabled,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedUsers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${theme.spacing.unit}px`,
    minHeight: 32,
    alignItems: 'flex-start',
    '& > *': {
      margin: 0, // Remove individual margins since we're using gap
    },
  },
  userChip: {
    maxWidth: '240px', // Slightly larger for better readability
    height: 28,
    fontSize: '0.8rem',
    transition: 'all 0.2s ease-in-out',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      width: 16,
      height: 16,
      margin: '0 4px 0 -4px',
    },
    '&:hover': {
      boxShadow: theme.shadows[1],
      transform: 'translateY(-1px)',
    },
  },
});

export default withStyles(styles, { withTheme: true })(InventoryUserAutocomplete); 